import React, { useState, useEffect } from "react";
import { Radio, Alert, Row, Col, Select } from "antd";
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
// graphql
import GET_PROGRAMS from "graphql/queries/GetPrograms";
import GET_ACTIVE_SUBSCRIPTION from "graphql/queries/GetActiveSubscription";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";
import GET_MY_SETTINGS from "graphql/queries/GetMySettings";

// common
import { SectionHeader } from "common/layout";
import {
  ProgramSelectorContainer,
  ProgramInfoContainer,
  ProgramDescription,
  Price,
  OutputPrice,
  CurrencySign,
  PerUnitTime,
  SubscriptionStatusTag,
} from "common/program_selector";
import { H4 } from "common/text";
// hooks
import useLocale from "hooks/useLocale";

// styling
import {
  ClassTypeSelector,
  CurrencySelector,
} from "pages/registration/SelectProgramFormStyle";
import screenSize from "utils/screenSize";

// types
import { RadioChangeEvent } from "antd/lib/radio";
import { Program, PaymentMethod } from "types";
import getCurrencySign from "utils/currency";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import { SelectValue } from "antd/lib/select";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import { CLASS_TYPES, SUPPORTED_CURRENCIES } from "utils/constants";
import { ProgramOption } from "../styles";
import ConfirmModal from "../ConfirmModal";
import PaymentModal from "../PaymentMethodModal";

const { Option } = Select;

type ChangeSubscriptionProps = {
  currency: SelectValue | undefined;
  setCurrency: (value: string) => void;
  currentCurrency?: string;
  defaultPaymentMethod?: PaymentMethod;
  paymentType?: string;
};

const ChangeSubscription = ({
  currency,
  setCurrency,
  currentCurrency,
  defaultPaymentMethod,
  paymentType,
}: ChangeSubscriptionProps) => {
  const { t } = useTranslation("my_credits");

  const [chosenProgramId, setChosenProgramId] = useState<string | undefined>(
    undefined
  );
  const [chosenProgram, setChosenProgram] = useState<Program | undefined>(
    undefined
  );
  const [programType, setProgramType] = useState<string | undefined>(undefined);
  const language = useLocale();
  const isVnPay = paymentType === "vnpay";
  const filter = isVnPay ? ["vnpay"] : ["my-subscription"];
  const CURRENCIES = isVnPay
    ? [{ name: "VND", value: "vnd", locale: ["vi"] }]
    : SUPPORTED_CURRENCIES;
  const { data: userSettings } = useQuery(GET_MY_SETTINGS, {
    fetchPolicy: "network-only",
  });
  const [studyLanguageId, setStudyLanguageId] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    if (userSettings?.me?.currentLanguage?.id) {
      setStudyLanguageId(userSettings.me.currentLanguage.id);
    }
  }, [userSettings]);

  const { data, loading } = useQuery(GET_PROGRAMS, {
    variables: {
      filter,
      currency,
      language,
      studyLanguageId,
    },
  });
  const { data: activeSubsData, loading: activeSubsLoading } = useQuery(
    GET_ACTIVE_SUBSCRIPTION
  );
  const [updateMySettings] = useMutation(UPDATE_PROFILE);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const updateClassTypeSetting = async () => {
    if (userSettings?.me && programType) {
      const activeClassType = CLASS_TYPES.find(
        (classType: { code: number; name: string }) => {
          return classType.name.toLowerCase().includes(programType);
        }
      );
      if (
        activeClassType &&
        userSettings.me.preferClassType !== activeClassType.code
      ) {
        await updateMySettings({
          variables: {
            input: {
              attributes: { preferClassType: activeClassType.code },
            },
          },
        });
      }
    }
  };

  if (loading || activeSubsLoading) {
    return <Alert type="info" message={t("Loading...")} />;
  }

  const { activeSubscription } = activeSubsData.me;
  let activeProgramId: string = activeSubscription?.stripePriceId;
  const activeProgramType: string | undefined =
    activeSubscription &&
    ["group", "1-on-1"].filter((type: string) => {
      const filters: string[] = activeSubscription?.filter;
      return filters.includes(type);
    })[0];

  const FALLBACK_PROGRAM_TYPE = "group";
  // Set default values
  if (!programType) {
    // set the selector program type to one of active subscription
    setProgramType(activeProgramType || FALLBACK_PROGRAM_TYPE);
  }
  if (!chosenProgramId && activeProgramId) {
    setChosenProgramId(activeProgramId);
  }

  const allPrograms = data.plans;
  // Filter by program type

  const filteredPrograms = allPrograms.filter(
    (program: Program) => programType && program.filter.includes(programType)
  );

  // Sort by price DESC
  const standardPrograms = filteredPrograms
    .filter(
      (program: Program) =>
        program.filter.includes("standard") && !program.filter.includes("trial")
    )
    .slice()
    .sort((plan1: Program, plan2: Program) => plan2.price - plan1.price);
  const intensivePrograms = filteredPrograms
    .filter(
      (program: Program) =>
        program.filter.includes("intensive") &&
        !program.filter.includes("trial")
    )
    .slice()
    .sort((plan1: Program, plan2: Program) => plan2.price - plan1.price);

  const renderProgramOptions = (programs: Program[], subscription: boolean) => {
    // if no plans found
    if (programs.length === 0 && !isVnPay) {
      return (
        <div style={{ marginTop: 18 }}>
          <Alert type="info" message={t("No program found.")} />
        </div>
      );
    }

    const ActiveTag = (
      <SubscriptionStatusTag style={{ display: "inline" }}>
        {t("ACTIVE")}
      </SubscriptionStatusTag>
    );
    const PausedTag = (
      <SubscriptionStatusTag style={{ display: "inline" }} gray>
        {t("CANCELED")}
      </SubscriptionStatusTag>
    );

    const renderSubscriptionStatusTag = () => {
      if (activeSubscription?.renew || isVnPay) return ActiveTag;
      return PausedTag;
    };

    const chooseSubscriptionButNotPaymentMethod = () => {
      if (!defaultPaymentMethod && !isVnPay) {
        // when options are disabled due to no default payment method
        setPaymentVisible(true);
      }
    };

    if (!activeSubscription?.renew && !isVnPay) activeProgramId = "-1"; // when a subscription is not renewed anymore, the only information to be displayed is the time until the expiry date. Other information regarding the temporary option is treated as equal to other options

    return (
      // whenever the use stop subscribing, display no active subscription by uncheck all program option (including the temporarily active one as it will expire by the end of the current non-renewed subscription)
      <ProgramSelectorContainer
        value={activeSubscription?.renew ? chosenProgramId : -1}
        disabled={!defaultPaymentMethod && !isVnPay}
      >
        {programs.map((program: Program) => (
          <div
            onClick={chooseSubscriptionButNotPaymentMethod}
            aria-hidden="true"
          >
            <ProgramOption
              key={program.id}
              value={program.id}
              checked={
                program.id === chosenProgramId &&
                (activeSubscription?.renew || isVnPay) // only display checked when it is renewed, else there is no acgive subcription
              }
              onChange={() => {
                setChosenProgramId(program.id);
                setChosenProgram(program);
                setModalVisible(true);
              }}
            >
              <ProgramInfoContainer gutter={[10, 10]}>
                <ProgramDescription
                  sm={16}
                  xs={12}
                  style={{ flexWrap: "wrap" }}
                >
                  <div style={{ whiteSpace: "normal" }}>
                    {program.name}
                    {program.id === activeProgramId &&
                      renderSubscriptionStatusTag()}
                  </div>
                  {screenSize() === "mobile" && (
                    <div style={{ flexBasis: "100%" }} />
                  )}
                </ProgramDescription>
                <Price
                  sm={8}
                  xs={12}
                  style={{
                    fontSize: resizeOverflowingPrice(
                      outputManuallySetPrice(program.price)
                    ),
                  }}
                >
                  <CurrencySign>
                    <span // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: getCurrencySign(currency?.toString() || ""),
                      }}
                    />
                  </CurrencySign>
                  <OutputPrice>
                    {outputManuallySetPrice(program.price)}
                  </OutputPrice>
                  <PerUnitTime>
                    {subscription && !isVnPay && t("/month")}
                  </PerUnitTime>
                </Price>
              </ProgramInfoContainer>
            </ProgramOption>
          </div>
        ))}
      </ProgramSelectorContainer>
    );
  };

  const renderHeader = () => {
    if (!!activeSubscription && !isVnPay) {
      // eslint-disable-next-line no-nested-ternary
      return activeSubscription.renew
        ? t("CHANGE SUBSCRIPTION")
        : t("SUBSCRIBE AGAIN");
    }

    return t("CHOOSE A SUBSCRIPTION");
  };

  return (
    <div>
      <Row
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Col md={17} sm={24}>
          <SectionHeader>{renderHeader()}</SectionHeader>
          {!defaultPaymentMethod && (
            <p>
              {t(
                "If you currently have no active subscription, you can choose a new one here. Once confirmed by you, it will become active immediately."
              )}
            </p>
          )}
        </Col>
        {!currentCurrency && (
          <Col md={2} sm={8}>
            <CurrencySelector
              value={currency}
              onChange={(value) => setCurrency(value as string)}
            >
              {CURRENCIES.map(({ value, name }) => (
                <Option value={value} key={value}>
                  {name}
                </Option>
              ))}
            </CurrencySelector>
          </Col>
        )}
        <Col md={4} sm={8}>
          <ClassTypeSelector
            value={programType}
            onChange={(ev: RadioChangeEvent) => {
              setProgramType(ev.target.value);
            }}
          >
            <Radio.Button value="group">{t("Group")}</Radio.Button>
            <Radio.Button value="1-on-1">{t("1-on-1")}</Radio.Button>
          </ClassTypeSelector>
        </Col>
      </Row>
      <br />
      <Row gutter={20}>
        <Col md={12} sm={24}>
          {!isVnPay && <H4>{t("SUBCRIPTIONS - Pay Every 4 Weeks")}</H4>}
          {renderProgramOptions(intensivePrograms, true)}
        </Col>
        <Col md={12} sm={24}>
          {!isVnPay && <H4>{t("PACKAGES - One-Off Payment")}</H4>}
          {renderProgramOptions(standardPrograms, false)}
        </Col>
      </Row>
      <br />

      {/* When default payment method is lacked and a disabled option is clicked */}
      <PaymentModal
        visible={paymentVisible}
        onCloseModal={() => setPaymentVisible(false)}
      />

      {/* This button is only clickable if the selector is dirty
      Also disable if user doesn't have default payment method */}
      <ConfirmModal
        disabled={
          chosenProgramId === activeProgramId ||
          (!defaultPaymentMethod && !isVnPay)
        }
        chosenProgramId={chosenProgramId}
        chosenProgram={chosenProgram}
        activeSubscription={activeSubscription}
        updateClassTypeSetting={updateClassTypeSetting}
        defaultPaymentMethod={defaultPaymentMethod}
        visible={modalVisible && chosenProgramId !== activeProgramId}
        setVisible={(status) => setModalVisible(status)}
        onCloseModal={() => {
          setChosenProgramId(activeProgramId);
          setModalVisible(false);
        }}
        paymentType={paymentType}
      />
    </div>
  );
};

export default ChangeSubscription;
